import React, { memo, use, useCallback, useMemo } from "react";
import { StackNavigationOptions, StackScreenProps } from "@react-navigation/stack";
import {
    CardStyleInterpolators,
    HeaderStyleInterpolators,
    StackCardStyleInterpolator,
} from "@react-navigation/stack";
import {
    createPlatformStackNavigator,
    PlatformStackScreenOptions,
} from "./createPlatformStackNavigator";
import { PDF_SHEET_CONTENT, PDF_SHEET_PLATFORMS } from "./pdf-sheet";
import { useNavigation } from "@react-navigation/native";
import { AuthManagerContext, AuthStatus } from "../contexts/AuthManager";
import { useTheme } from "../hooks/use-theme";
import * as Device from "expo-device";
import CheckClientInfo from "../screens/auth/CheckClientInfo";
import WorkerHomeTab from "./worker/Home";
import ClientHomeTab from "./client/Home";
import CreateMissionStack from "./CreateMissionStack";
import { useI18n } from "../hooks/i18n-hooks";
import WelcomeScreen from "../screens/auth/Welcome";
import EmailVerifiedSuccessScreen from "../screens/auth/EmailVerifiedSuccess";
import EmailVerifiedErrorScreen from "../screens/auth/EmailVerifiedError";
import TimesheetWorkerInput from "../screens/user/TimesheetWorkerInput";
import TimesheetClientReviewScreen from "../screens/client/TimesheetClientReview";
import TimesheetWorkerReviewScreen from "../screens/user/TimesheetWorkerReview";
import PdfViewerScreen from "../screens/PdfViewerScreen";
import DocumentEditorScreen from "../components/document/DocumentEditorScreen";
import { KycStatus, ProfileType } from "../__generated__/graphql";
import SalaryCountingDetailsScreen from "../screens/SalaryCountingDetails";
import WorkerProfileEditorScreen from "../screens/worker/WorkerProfileEditor";
import SendInvitationScreen from "../screens/client/SendInvitationScreen";
import ConfigureLppScreen from "../screens/user/ConfigureLpp";
import MissionTerminationScreen from "../screens/user/MissionTermination";
import ContractSigningScreen from "../screens/user/ContractSigningScreen";
import { RootStackParamList } from "./RootNavigation";
import MissionTimeSheetScreen from "../screens/user/MissionTimeSheetScreen";
import ResendEmailVerification from "../screens/user/ResendEmailVerificationScreen";
import { Easing, Platform, Pressable } from "react-native";
import Icon, { IconName } from "../components/Icon";
import BankAccountEditorScreen from "../screens/profile-editor/BankAccountEditor";
import ClientProfileEditorScreen from "../screens/client/ClientProfileEditor";
import AccountEditorScreen from "../screens/user/AccountEditor";
import ClientEditorScreen from "../screens/user/ClientEditor";
import ClientContactEditorScreen from "../screens/user/ClientContactEditor";
import { useCardStyleInterpolator } from "../hooks/use-card-style-interpolator";
import NotificationsModal from "../components/NotificationsModal";
import ClientListScreen from "../screens/worker/ClientListScreen";
import SalaryDetailsScreen from "../screens/worker/SalaryDetails";
import InvitationScreen from "../screens/Invitation";
import InvitationLinkScreen from "../screens/InvitationLink";
import FeesDetailsScreen from "../screens/FeesDetails";
import InvoiceDetailsScreen from "../screens/client/InvoiceDetails";
import { NotFoundScreen } from "../screens/NotFoundScreen";
import { useModalCardStyleInterpolator } from "../hooks/use-modal-card-style-interpolator";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import PreIdVerification from "../screens/auth/PreIdVerification";
import IdVerificationPending from "../screens/auth/IdVerificationPending";
import IdVerificationRetry from "../screens/auth/IdVerificationRetry";
import VeriffScreen from "../screens/user/VeriffScreen";
import SkribbleSampleScreen from "../screens/user/SkribbleSampleScreen";
import CctDetailsScreen from "../screens/user/CctDetails";
import { useAtom } from "jotai";
import { notificationEnabledState } from "../atoms/push-notifications";
import PostOnboardingProfileEditorScreen from "../screens/profile-editor/PostOnboardingProfileEditor";
import CivilStateEditorScreen from "../screens/profile-editor/CivilStateEditor";
import ChildrenEditorScreen from "../screens/profile-editor/ChildrenEditor";
import { ModalScreenWrapper } from "./ModalScreenWrapper";
import FiscalAttestationEditorScreen from "../screens/profile-editor/FiscalAttestationEditor";
import { usePostOnboardingData } from "../hooks/use-postonboarding-data";
import SignupFinishedScreen from "../screens/auth/SignupFinished";
import PostOnboardingPrepareScreen from "../screens/auth/PostOnboardingPrepare";
import PayrollBreakdownListScreen from "../screens/user/PayrollBreakdownListScreen";
import PayrollBreakdownDetailsScreen from "../screens/user/PayrollBreakdownDetailsScreen";
import PayrollBreakdownEditorScreen from "../screens/client/PayrollBreakdownEditorScreen";
import PayrollBreakdownTimeSheetScreen from "../screens/user/PayrollBreakdownTimeSheetScreen";
import EmailEditorScreen from "../screens/profile-editor/EmailEditor";
import HomeAddressEditorScreen from "../screens/profile-editor/HomeAddressEditor";
import PhoneNumberEditorScreen from "../screens/user/PhoneNumberEditor";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import CheckPrivateClientInfo from "../screens/auth/CheckPrivateClientInfo";
import InviteClientInfoScreen from "../screens/worker/InviteClientInfo";
import SignupSigninStack from "./SignupSigninStack";
import VerificationFailed from "../screens/auth/VerificationFailed";
import CheckWorkerInfo from "../screens/auth/CheckWorkerInfo";
import IJMEditorNavScreen from "../screens/client/IJMEditorNavScreen";
import LPPEditorNavScreen from "../screens/client/LPPEditorNavScreen";
import VacationEditorNavScreen from "../screens/client/VacationEditorNavScreen";
import ContractPreviewScreen from "../screens/user/ContractPreviewScreen";
import MissionSelectSignerScreen from "../screens/user/MissionSelectSignerScreen";
import IJMSelectionViewer from "../components/editors/ijm/screens/IJMSelectionViewer";
import ExperiencesEditorScreen from "../screens/profile-editor/ExperiencesEditor";
import { capitalizeEachFirstLetter } from "../utils";
import { useScreenListeners } from "../hooks/use-screen-listeners";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import AvsNumberEditorScreen from "../screens/worker/AvsNumberEditor";
import ScreenHeader from "../screens/user/ScreenHeader";
import MissionAnnexesScreen from "../screens/user/MissionAnnexes";
import SalaryPayslips from "../screens/worker/SalaryPayslips";
import PayslipDetailsScreen from "../screens/worker/PayslipDetails";
import AmendmentCancellationScreen from "../screens/user/AmendmentCancellation";
import CreateContractPreSign from "../screens/create-mission/CreateContractPreSign";
import MissionEditScreen from "../screens/user/MissionEdit";

const Stack = createPlatformStackNavigator<RootStackParamList>();

const headerBackImage = (icon: IconName = "back") => {
    return (
        <Icon
            name={icon}
            size={28}
            style={{
                marginLeft: Platform.OS === "ios" ? 10 : 0,
                marginRight: 5,
            }}
        />
    );
};

// Screen appears instantly (no transition) on both engines.
const noTransition = (): PlatformStackScreenOptions =>
    (Platform.OS === "web"
        ? { cardStyleInterpolator: CardStyleInterpolators.forNoAnimation }
        : { animation: "none" }) as PlatformStackScreenOptions;

// Native modals have no header back button by default, so a close button dismisses them (the web
// `headerBackImage` only exists on the JS stack). Rendered only when the screen shows a header.
const ModalCloseButton = () => {
    const navigation = useNavigation();
    return (
        <Pressable onPress={() => navigation.goBack()} testID="button-close">
            {headerBackImage("close")}
        </Pressable>
    );
};

// Native presents a real modal (with a close button; dismissed via that, swipe or back); web keeps
// the JS-stack card slide-up with a slide-up header. `extraWebOptions` carries JS-stack-only extras
// (close icon, transparent dialog).
//
// `sheet` presents the screen as a native form sheet (a Material bottom sheet on Android, a
// form/page sheet on iOS) instead of a full-screen modal. Pass `true` for both platforms, or
// `{ ios, android }` to opt in per platform. Use ONLY for read-only screens: a form sheet is
// always swipe-to-dismiss and there is no way to block that gesture (react-native-screens hardcodes
// isDraggable/isHideable, and native-stack's preventNativeDismiss / `beforeRemove` guard only covers
// iOS + the Android hardware back button, not the sheet drag).
const modalScreen = (
    webCardStyleInterpolator: StackCardStyleInterpolator,
    extraWebOptions: StackNavigationOptions = {},
    sheet: boolean | { [k in typeof Platform.OS]?: boolean } = false,
): PlatformStackScreenOptions => {
    const useSheet = typeof sheet === "boolean" ? sheet : (sheet[Platform.OS] ?? false);
    return (
        Platform.OS === "web"
            ? {
                  cardStyleInterpolator: webCardStyleInterpolator,
                  headerStyleInterpolator: HeaderStyleInterpolators.forSlideUp,
                  ...extraWebOptions,
              }
            : {
                  presentation: useSheet ? "formSheet" : "modal",
                  // Android's default modal animation is a horizontal push (slide from the right), so a
                  // modal reads like a normal screen. Force the bottom slide so "modal == comes from the
                  // bottom" everywhere. iOS `modal`/`formSheet` already present from the bottom; form
                  // sheets on Android are already a bottom sheet, so only the full modal needs this.
                  ...(Platform.OS === "android" && !useSheet
                      ? { animation: "slide_from_bottom" as const }
                      : {}),
                  headerLeft: () => <ModalCloseButton />,
              }
    ) as PlatformStackScreenOptions;
};

// Shared 300ms fade+scale timing so navigation modals animate EXACTLY like the JS Modal dialog
// (components/ModalPortal.tsx: WEB_DIALOG_ANIMATION_DURATION + matching cubic easing). Applied only on
// large (desktop web), where the centered-dialog look is used.
const WEB_MODAL_TRANSITION_SPEC: StackNavigationOptions["transitionSpec"] = {
    open: { animation: "timing", config: { duration: 300, easing: Easing.out(Easing.cubic) } },
    close: { animation: "timing", config: { duration: 300, easing: Easing.in(Easing.cubic) } },
};

const Navigator = () => {
    const theme = useTheme();
    const i18n = useI18n();
    const isLarge = useIsScreenSizeMin("large");
    const cardStyleInterpolator = useCardStyleInterpolator();
    const modalCardStyleInterpolator = useModalCardStyleInterpolator();

    const { screenListeners, routeName = "Welcome" } =
        useScreenListeners<keyof RootStackParamList>();

    const { status, user } = use(AuthManagerContext);
    const { profileType } = use(ProfileSwitcherManagerContext);
    const [notificationEnabled, setNotificationEnabled] = useAtom(notificationEnabledState);

    const isNotificationsPermissionsModalOpen =
        Device.isDevice &&
        Platform.OS !== "web" &&
        notificationEnabled === undefined &&
        (routeName === "IdVerificationPending" || routeName === "Home");

    const onNotificationsFinished = useCallback(
        (result: boolean) => {
            setNotificationEnabled(result);
        },
        [setNotificationEnabled],
    );

    const getModalScreenConfig = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (Component: React.FC<any>, title: string | ((params: any) => string)) => {
            return {
                // eslint-disable-next-line react/display-name
                component: memo((props: StackScreenProps<RootStackParamList>) => (
                    <ModalScreenWrapper
                        component={Component}
                        title={typeof title === "string" ? title : title(props.route.params)}
                        {...props}
                    />
                )),
                // `title` can be a function of the route params (e.g. SendInvitation). React
                // Navigation's `title` option must resolve to a string, so resolve it here via the
                // options-as-function form rather than passing the raw function (which would be
                // rendered as a React child and throw).
                // Native presents a real modal; web keeps the JS-stack modal card + slide-up header,
                // rendering as a centered transparent dialog on large screens.
                options: ({ route }: { route: StackScreenProps<RootStackParamList>["route"] }) =>
                    ({
                        title: typeof title === "string" ? title : title(route.params),
                        headerShown: true,
                        gestureEnabled: false,
                        ...modalScreen(modalCardStyleInterpolator, {
                            headerBackButtonDisplayMode: "minimal",
                            headerBackImage: () => headerBackImage("close"),
                            ...(isLarge
                                ? {
                                      detachPreviousScreen: false,
                                      headerShown: false,
                                      animation: "default",
                                      transitionSpec: WEB_MODAL_TRANSITION_SPEC,
                                      cardStyle: {
                                          backgroundColor: "transparent",
                                      },
                                  }
                                : {}),
                        }),
                    }) as PlatformStackScreenOptions,
            };
        },
        [isLarge, modalCardStyleInterpolator],
    );

    const modalScreensProps = useMemo(
        () => ({
            SendInvitation: getModalScreenConfig(SendInvitationScreen, (params) =>
                i18n.t(`invitation.send.${params.target}.title`),
            ),
            InviteClientInfo: getModalScreenConfig(
                InviteClientInfoScreen,
                i18n.t("screens.invite_client.title"),
            ),
            CivilStateEdit: getModalScreenConfig(
                CivilStateEditorScreen,
                i18n.t("screens.profile_editor.civil_state.title"),
            ),
            FiscalAttestationEdit: getModalScreenConfig(
                FiscalAttestationEditorScreen,
                i18n.t("screens.profile_editor.fiscal_attestation.title"),
            ),
            ExperiencesEdit: getModalScreenConfig(
                ExperiencesEditorScreen,
                i18n.t("screens.profile_editor.experiences.title"),
            ),
            EmailEdit: getModalScreenConfig(EmailEditorScreen, (params) =>
                i18n.t(
                    params?.businessEmail
                        ? "screens.profile_editor.email.title_business"
                        : "screens.profile_editor.email.title",
                ),
            ),
            HomeAddressEdit: getModalScreenConfig(
                HomeAddressEditorScreen,
                i18n.t("screens.profile_editor.home_address.title"),
            ),
            ChildrenEdit: getModalScreenConfig(
                ChildrenEditorScreen,
                i18n.t("screens.profile_editor.children.title"),
            ),
            BankAccountEdit: getModalScreenConfig(
                BankAccountEditorScreen,
                i18n.t("screens.profile_editor.bank_account.title"),
            ),
            SalaryCountingDetails: getModalScreenConfig(
                SalaryCountingDetailsScreen,
                i18n.t("screens.user.salary_counting_details.screen_title"),
            ),
            FeesDetails: getModalScreenConfig(
                FeesDetailsScreen,
                i18n.t("screens.fees_and_deductions.title"),
            ),
        }),
        [getModalScreenConfig, i18n],
    );

    const { isValid: isPostOnboardingValid } = usePostOnboardingData();
    // Until its onboarding is complete, a worker profile only gets the onboarding screens: the app
    // screens are not registered at all, so a deeplink or a web URL to one of them (e.g.
    // app/missions) falls back to the first registered screen (PreIdVerification or
    // PostOnboardingPrepare) instead of opening it.
    const isWorkerOnboardingIncomplete =
        profileType === ProfileType.Worker && !isPostOnboardingValid;

    const insets = useSafeAreaInsets();

    return (
        <>
            <NotificationsModal
                visible={isNotificationsPermissionsModalOpen}
                onFinished={onNotificationsFinished}
            />
            <Stack.Navigator
                screenListeners={screenListeners as never}
                screenOptions={{
                    headerStyle: {
                        backgroundColor: theme.colors.background,
                    },
                    headerBackButtonDisplayMode: "minimal",
                    headerTitleAlign: "center",
                    gestureEnabled: true,
                    headerShadowVisible: false,
                }}
                // JS-stack-only header/card options; on native the default headers use the system
                // back button and native title style, and native drives its own push animations.
                webScreenOptions={{
                    headerMode: "screen",
                    headerStatusBarHeight: insets.top,
                    headerBackImage: () => headerBackImage(),
                    headerBackTestID: "button-back",
                    headerTitleStyle: {
                        ...theme.fonts.titleMedium,
                    },
                    cardStyleInterpolator,
                    headerStyleInterpolator: HeaderStyleInterpolators.forUIKit,
                    cardStyle: {
                        height: "100%",
                    },
                }}
            >
                {status === AuthStatus.Out && (
                    <Stack.Group>
                        <Stack.Screen
                            name="Welcome"
                            component={WelcomeScreen}
                            options={{
                                title: i18n.t("screens.auth.presentation_carousel.step_0.title"),
                                headerShown: false,
                                ...noTransition(),
                            }}
                        />
                        <Stack.Screen
                            name="SignupSigninStack"
                            component={SignupSigninStack}
                            options={
                                {
                                    headerShown: false,
                                    gestureEnabled: false,
                                    // reveal_from_bottom has no native-stack equivalent; native
                                    // slides up from the bottom instead.
                                    ...(Platform.OS === "web"
                                        ? {
                                              cardStyleInterpolator:
                                                  CardStyleInterpolators.forRevealFromBottomAndroid,
                                          }
                                        : { animation: "slide_from_bottom" }),
                                } as PlatformStackScreenOptions
                            }
                        />
                        <Stack.Screen
                            name="CheckWorkerInfo"
                            component={CheckWorkerInfo}
                            options={{
                                title: i18n.t("screens.auth.check_worker_info.title"),
                            }}
                        />
                    </Stack.Group>
                )}
                {status === AuthStatus.In &&
                    user &&
                    (user?.kycStatus?.status === KycStatus.Rejected ? (
                        <Stack.Screen
                            name="IdVerificationRetry"
                            component={IdVerificationRetry}
                            options={{
                                title: i18n.t("screens.auth.idVerificationRetry.title"),
                                headerShown: false,
                            }}
                        />
                    ) : [KycStatus.Pending, KycStatus.Submitted].includes(
                          user?.kycStatus?.status || KycStatus.Init,
                      ) ? (
                        <Stack.Screen
                            // Pending and Submitted KycStatus are handled here
                            name="IdVerificationPending"
                            component={IdVerificationPending}
                            options={{
                                title: i18n.t("screens.auth.idVerificationPending.title"),
                                headerShown: false,
                            }}
                        />
                    ) : user.kycStatus?.status === KycStatus.Banned ? (
                        <Stack.Screen
                            // ID verification banned OR client business is rejected
                            name="VerificationFailed"
                            component={VerificationFailed}
                            options={{
                                title: i18n.t("screens.auth.verificationFailed.title"),
                                headerShown: false,
                            }}
                        />
                    ) : (
                        user &&
                        profileType && (
                            <Stack.Group
                                // Web keeps the JS-stack card interpolator as the group default;
                                // native uses its default push animation.
                                screenOptions={
                                    {
                                        headerShown: false,
                                        ...(Platform.OS === "web" ? { cardStyleInterpolator } : {}),
                                    } as PlatformStackScreenOptions
                                }
                            >
                                {profileType === ProfileType.Worker &&
                                user?.kycStatus?.status === KycStatus.Init ? (
                                    <>
                                        <Stack.Screen
                                            name="PreIdVerification"
                                            component={PreIdVerification}
                                            options={{
                                                title: i18n.t(
                                                    "screens.auth.preIdVerification.title",
                                                ),
                                                headerShown: false,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="VeriffScreen"
                                            component={VeriffScreen}
                                            options={{
                                                title: i18n.t("veriff.title"),
                                                headerShown: false,
                                            }}
                                        />
                                    </>
                                ) : isWorkerOnboardingIncomplete ? (
                                    <Stack.Screen
                                        name="PostOnboardingPrepare"
                                        component={PostOnboardingPrepareScreen}
                                        options={{
                                            title: i18n.t(
                                                "screens.auth.postOnboarding_more.sub_title",
                                            ),
                                            headerShown: false,
                                        }}
                                    />
                                ) : null}
                                {!isWorkerOnboardingIncomplete && (
                                    <>
                                        {profileType === ProfileType.Worker && (
                                            <Stack.Screen
                                                name="Home"
                                                component={WorkerHomeTab}
                                                options={{
                                                    title: i18n.t("home"),
                                                    ...noTransition(),
                                                }}
                                            />
                                        )}
                                        {profileType === ProfileType.Client && (
                                            <Stack.Screen
                                                name="Home"
                                                component={ClientHomeTab}
                                                options={{
                                                    title: i18n.t("home"),
                                                    ...noTransition(),
                                                }}
                                            />
                                        )}
                                        {profileType === ProfileType.Client &&
                                            user?.kycStatus?.status === KycStatus.Init && (
                                                <>
                                                    <Stack.Screen
                                                        name="PreIdVerification"
                                                        component={PreIdVerification}
                                                        options={{
                                                            title: i18n.t(
                                                                "screens.auth.preIdVerification.title",
                                                            ),
                                                            headerShown: false,
                                                        }}
                                                    />
                                                    <Stack.Screen
                                                        name="VeriffScreen"
                                                        component={VeriffScreen}
                                                        options={{
                                                            title: i18n.t("veriff.title"),
                                                            headerShown: false,
                                                        }}
                                                    />
                                                </>
                                            )}
                                        <Stack.Screen
                                            name="SignupFinished"
                                            component={SignupFinishedScreen}
                                            options={{
                                                title: i18n.t(`screens.auth.signupFinished.title`, {
                                                    name: capitalizeEachFirstLetter(
                                                        user?.firstName || "",
                                                    ),
                                                }),
                                                headerShown: false,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="SkribbleSampleScreen"
                                            component={SkribbleSampleScreen}
                                            options={{
                                                headerShown: true,
                                                title: "Sample skribble",
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ResendEmailVerification"
                                            component={ResendEmailVerification}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t(
                                                    "screens.user.resend_verification_email.title",
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="WorkerProfileEdit"
                                            component={WorkerProfileEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.worker_profile_edit.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="AvsNumberEdit"
                                            component={AvsNumberEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("profile.avs_number"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="PhoneNumberEdit"
                                            component={PhoneNumberEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.phone_number_edit.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ClientProfileEdit"
                                            component={ClientProfileEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.client_profile_edit.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="IJMSelectionViewer"
                                            component={IJMSelectionViewer}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("ijm.screen_title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ClientEdit"
                                            component={ClientEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.client_edit.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ClientContactEdit"
                                            component={ClientContactEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.client_contact_edit.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="AccountEdit"
                                            component={AccountEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.account_edit.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="BankAccountEdit"
                                            component={BankAccountEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t(
                                                    "screens.profile_editor.bank_account.title",
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ExperiencesEdit"
                                            component={ExperiencesEditorScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t(
                                                    "screens.profile_editor.experiences.title",
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ConfigureLpp"
                                            component={ConfigureLppScreen}
                                            options={{
                                                title: i18n.t("screens.configure_lpp.title"),
                                                headerShown: true,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="CctDetails"
                                            component={CctDetailsScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t("screens.cct_details.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="MissionEdit"
                                            component={MissionEditScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t("mission.edit.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="MissionAnnexes"
                                            component={MissionAnnexesScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t("screens.mission_annexes.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ContractSigningScreen"
                                            component={ContractSigningScreen}
                                            options={{
                                                headerShown: true,
                                                headerBackTitle: i18n.t("cancel"),
                                                headerBackButtonDisplayMode: "minimal",
                                                title: i18n.t("screens.contract_signing.title"),
                                                gestureEnabled: false,
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="ContractPreviewScreen"
                                            component={ContractPreviewScreen}
                                            options={{
                                                headerShown: true,
                                                headerBackTitle: i18n.t("cancel"),
                                                headerBackButtonDisplayMode: "minimal",
                                                title: i18n.t("screens.contract_preview.title"),
                                                gestureEnabled: false,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="MissionSelectSignerScreen"
                                            component={MissionSelectSignerScreen}
                                            options={{
                                                headerShown: false,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="CreateMissionStack"
                                            component={CreateMissionStack}
                                            options={{
                                                headerShown: false,
                                                gestureEnabled: false,
                                                ...modalScreen(
                                                    modalCardStyleInterpolator,
                                                    isLarge
                                                        ? {
                                                              detachPreviousScreen: false,
                                                              animation: "default",
                                                              transitionSpec:
                                                                  WEB_MODAL_TRANSITION_SPEC,
                                                              cardStyle: {
                                                                  backgroundColor: "transparent",
                                                              },
                                                          }
                                                        : {},
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="MissionTimeSheets"
                                            component={MissionTimeSheetScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t("timesheet_list.title"),
                                                gestureEnabled: true,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="MissionTermination"
                                            component={MissionTerminationScreen}
                                            options={{
                                                headerShown: true,
                                                title: "", // Title is set dyamically from the MissionTermination screen
                                                gestureEnabled: false,
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                    {
                                                        headerBackImage: () =>
                                                            headerBackImage("close"),
                                                    },
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="CreateContractPreSign"
                                            component={CreateContractPreSign}
                                            options={{
                                                gestureEnabled: true,
                                                headerShown: false,
                                                title: i18n.t("mission.amendment.title"),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="AmendmentCancellation"
                                            component={AmendmentCancellationScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.reject_amendment.title"),
                                                gestureEnabled: false,
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                    {
                                                        headerBackImage: () =>
                                                            headerBackImage("close"),
                                                    },
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="TimesheetWorkerInput"
                                            component={TimesheetWorkerInput}
                                            options={{
                                                headerShown: false,
                                                gestureEnabled: false,
                                                title: i18n.t("time_sheet.nav_title"),
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="TimesheetClientReview"
                                            component={TimesheetClientReviewScreen}
                                            options={{
                                                title: i18n.t("time_sheet.nav_title"),
                                                headerShown: false,
                                                gestureEnabled: false,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="TimesheetWorkerReview"
                                            component={TimesheetWorkerReviewScreen}
                                            options={{
                                                title: i18n.t("time_sheet.nav_title"),
                                                headerShown: false,
                                                gestureEnabled: false,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="SalaryCountingDetails"
                                            {...modalScreensProps.SalaryCountingDetails}
                                        />
                                        <Stack.Screen
                                            name="PdfViewer"
                                            component={PdfViewerScreen}
                                            options={{
                                                headerShown: true,
                                                gestureEnabled: false,
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                    {
                                                        headerBackImage: () =>
                                                            headerBackImage("close"),
                                                    },
                                                ),
                                            }}
                                        />
                                        {/* DocumentEditor modal (its own ScreenHeader), opened imperatively via
                                            DocumentManager.editDocument/showDocument. Same modal config as
                                            CreateMissionStack: an animated centered dialog on desktop web, a
                                            native modal on iOS/Android — so it stacks above other modals. */}
                                        <Stack.Screen
                                            name="DocumentEditor"
                                            component={DocumentEditorScreen}
                                            options={{
                                                headerShown: false,
                                                gestureEnabled: false,
                                                ...modalScreen(
                                                    modalCardStyleInterpolator,
                                                    isLarge
                                                        ? {
                                                              detachPreviousScreen: false,
                                                              animation: "default",
                                                              transitionSpec:
                                                                  WEB_MODAL_TRANSITION_SPEC,
                                                              cardStyle: {
                                                                  backgroundColor: "transparent",
                                                              },
                                                          }
                                                        : {},
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="SendInvitation"
                                            {...modalScreensProps.SendInvitation}
                                        />
                                        <Stack.Screen
                                            name="InviteClientInfo"
                                            {...modalScreensProps.InviteClientInfo}
                                        />
                                        <Stack.Screen
                                            name="ClientList"
                                            component={ClientListScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.client_list.title"),
                                                gestureEnabled: true,
                                                header: ({ navigation }) => (
                                                    <ScreenHeader
                                                        label={i18n.t("screens.client_list.title")}
                                                        onLeftAction={navigation.goBack}
                                                        useHeaderButton
                                                        useSmallLabel
                                                    />
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="SalaryDetails"
                                            component={SalaryDetailsScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.user.salaries.salary_sheet"),
                                                gestureEnabled: true,
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                    {
                                                        headerBackImage: () =>
                                                            headerBackImage("close"),
                                                    },
                                                    PDF_SHEET_PLATFORMS,
                                                ),
                                                ...PDF_SHEET_CONTENT,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="SalaryPayslips"
                                            component={SalaryPayslips}
                                            options={{
                                                headerShown: true,
                                                title: "", // Title is set dynamically from the SalaryPayslips screen
                                                gestureEnabled: true,
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                    {
                                                        headerBackImage: () =>
                                                            headerBackImage("close"),
                                                    },
                                                    true,
                                                ),
                                            }}
                                        />
                                        <Stack.Screen
                                            name="PayslipDetails"
                                            component={PayslipDetailsScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t(
                                                    "screens.user.salaries.payslip_sheet",
                                                ),
                                                gestureEnabled: true,
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                    {
                                                        headerBackImage: () =>
                                                            headerBackImage("close"),
                                                    },
                                                    PDF_SHEET_PLATFORMS,
                                                ),
                                                ...PDF_SHEET_CONTENT,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="FeesDetails"
                                            {...modalScreensProps.FeesDetails}
                                        />
                                        <Stack.Screen
                                            name="InvoiceDetails"
                                            component={InvoiceDetailsScreen}
                                            options={{
                                                headerShown: true,
                                                title: i18n.t("screens.user.invoices.invoice"),
                                                gestureEnabled: true,
                                                ...modalScreen(
                                                    CardStyleInterpolators.forVerticalIOS,
                                                    {
                                                        headerBackImage: () =>
                                                            headerBackImage("close"),
                                                    },
                                                    PDF_SHEET_PLATFORMS,
                                                ),
                                                ...PDF_SHEET_CONTENT,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="PayrollBreakdownList"
                                            component={PayrollBreakdownListScreen}
                                            options={{
                                                title: i18n.t(
                                                    "screens.payroll_breakdown_list.title",
                                                ),
                                                headerShown: false,
                                                gestureEnabled: true,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="PayrollBreakdownDetails"
                                            component={PayrollBreakdownDetailsScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t("screens.payroll_breakdown.title"),
                                                gestureEnabled: true,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="PayrollBreakdownEditor"
                                            component={PayrollBreakdownEditorScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t(
                                                    "screens.payroll_breakdown_editor.title",
                                                ),
                                                gestureEnabled: true,
                                            }}
                                        />
                                        <Stack.Screen
                                            name="PayrollBreakdownTimeSheetScreen"
                                            component={PayrollBreakdownTimeSheetScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t(
                                                    "screens.payroll_breakdown.timesheets",
                                                ),
                                                gestureEnabled: true,
                                            }}
                                        />
                                        {/* IJMEditorNavScreen is used through use-ijm-editor.ts hook */}
                                        <Stack.Screen
                                            name="IJMEditorNavScreen"
                                            component={IJMEditorNavScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t("ijm.screen_title"),
                                                gestureEnabled: false,
                                                ...modalScreen(
                                                    modalCardStyleInterpolator,
                                                    isLarge
                                                        ? {
                                                              detachPreviousScreen: false,
                                                              headerShown: false,
                                                              animation: "default",
                                                              cardStyle: {
                                                                  backgroundColor: "transparent",
                                                              },
                                                          }
                                                        : {},
                                                ),
                                            }}
                                        />
                                        {/* LPPEditorNavScreen is used through use-lpp-editor.ts hook */}
                                        <Stack.Screen
                                            name="LPPEditorNavScreen"
                                            component={LPPEditorNavScreen}
                                            options={{
                                                headerShown: false,
                                                title: i18n.t("lpp.screen_title"),
                                                gestureEnabled: false,
                                                ...modalScreen(
                                                    modalCardStyleInterpolator,
                                                    isLarge
                                                        ? {
                                                              detachPreviousScreen: false,
                                                              headerShown: false,
                                                              animation: "default",
                                                              cardStyle: {
                                                                  backgroundColor: "transparent",
                                                              },
                                                          }
                                                        : {},
                                                ),
                                            }}
                                        />
                                        {/* VacationEditorNavScreen is used through use-vacation-editor.ts hook */}
                                        <Stack.Screen
                                            name="VacationEditorNavScreen"
                                            component={VacationEditorNavScreen}
                                            options={{
                                                gestureEnabled: false,
                                                ...modalScreen(modalCardStyleInterpolator),
                                                ...(isLarge
                                                    ? {
                                                          detachPreviousScreen: false,
                                                          headerShown: false,
                                                          animation: "default",
                                                          cardStyle: {
                                                              backgroundColor: "transparent",
                                                          },
                                                      }
                                                    : {}),
                                                headerShown: false,
                                                animation: "slide_from_bottom",
                                            }}
                                        />
                                        <Stack.Screen
                                            name="NotFoundScreen"
                                            component={NotFoundScreen}
                                            options={{
                                                title: i18n.t(
                                                    "screens.error_screen.not_found_title",
                                                ),
                                                headerShown: false,
                                            }}
                                        />
                                    </>
                                )}
                                <Stack.Screen
                                    name="CheckClientInfo"
                                    component={CheckClientInfo}
                                    options={{
                                        title: i18n.t("screens.auth.check_client_info.title"),
                                        headerShown: true,
                                        gestureEnabled: false,
                                    }}
                                />
                                <Stack.Screen
                                    name="CheckPrivateClientInfo"
                                    component={CheckPrivateClientInfo}
                                    options={{
                                        title: i18n.t(
                                            "screens.auth.check_private_client_info.title",
                                        ),
                                        headerShown: true,
                                        gestureEnabled: false,
                                    }}
                                />
                                <Stack.Screen
                                    name="PostOnboardingProfileEditor"
                                    component={PostOnboardingProfileEditorScreen}
                                    options={{
                                        headerShown: true,
                                        title: i18n.t("screens.profile_editor.title"),
                                    }}
                                />
                                <Stack.Screen
                                    name="CivilStateEdit"
                                    {...modalScreensProps.CivilStateEdit}
                                />
                                <Stack.Screen
                                    name="FiscalAttestationEdit"
                                    {...modalScreensProps.FiscalAttestationEdit}
                                />
                                <Stack.Screen
                                    name="ExperiencesEditModal"
                                    {...modalScreensProps.ExperiencesEdit}
                                />
                                <Stack.Screen name="EmailEdit" {...modalScreensProps.EmailEdit} />
                                <Stack.Screen
                                    name="HomeAddressEdit"
                                    {...modalScreensProps.HomeAddressEdit}
                                />
                                <Stack.Screen
                                    name="ChildrenEdit"
                                    {...modalScreensProps.ChildrenEdit}
                                />
                                <Stack.Screen
                                    name="BankAccountEditModal"
                                    {...modalScreensProps.BankAccountEdit}
                                />
                            </Stack.Group>
                        )
                    ))}
                <Stack.Screen
                    name="Invitation"
                    component={InvitationScreen}
                    options={{
                        header: ({ navigation }) => {
                            return (
                                <ScreenHeader
                                    useSmallLabel
                                    onLeftAction={() => navigation.goBack()}
                                    leftIcon="close"
                                    label={i18n.t("screens.invitation.title")}
                                />
                            );
                        },
                        headerShown: true,
                        ...modalScreen(CardStyleInterpolators.forVerticalIOS),
                    }}
                />
                <Stack.Screen
                    // The link of an invitation sent by email: handled signed in or not.
                    name="InvitationLink"
                    component={InvitationLinkScreen}
                    options={{
                        title: i18n.t("screens.invitation.title"),
                        headerShown: false,
                        ...noTransition(),
                    }}
                />
                <Stack.Screen
                    name="EmailVerifiedSuccess"
                    component={EmailVerifiedSuccessScreen}
                    options={{
                        title: i18n.t("screens.auth.email_verified.success.title"),
                        headerShown: false,
                        ...noTransition(),
                    }}
                />
                <Stack.Screen
                    name="EmailVerifiedError"
                    component={EmailVerifiedErrorScreen}
                    options={{
                        title: i18n.t("screens.auth.email_verified.error.title"),
                        headerShown: false,
                        ...noTransition(),
                    }}
                />
            </Stack.Navigator>
        </>
    );
};

export default Navigator;
